import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { getMilestoneInteractionsByType } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Utils";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { useRecordMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { StyledIconByName } from "@faire/retailer-visitor-shared/views/product/utils/StyledIconByName";
import { optimizeImageUrl, optimizeImageUrlHeight } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Close } from "@faire/web--source/slate/icons/Close";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import styled from "styled-components";
import { generateCSSBreakpointMapForImages } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/utils";
var DESKTOP_IMAGE_WIDTH = 584;
var MOBILE_IMAGE_HEIGHT = 160;
var ListItem = function(param) {
    var listItem = param.listItem;
    var _listItem_icon;
    return /*#__PURE__*/ _jsxs(Row, {
        children: [
            /*#__PURE__*/ _jsx(IconWrapper, {
                children: /*#__PURE__*/ _jsx(StyledIconByName, {
                    name: (_listItem_icon = listItem.icon) === null || _listItem_icon === void 0 ? void 0 : _listItem_icon.type,
                    fontSize: "16px",
                    color: Core.icon.default,
                    strokeWidth: 1
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: listItem.description_text
            })
        ]
    });
};
export var MarketingBanner = function() {
    var _this, _this1, _layoutElement_layout_element_data;
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onCTAClick = _React_useContext.onCTAClick, requestId = _React_useContext.requestId;
    var isSSR = useIsSSR();
    var isTabletAndAbove = useViewport().isTabletAndAbove;
    var _useState = _sliced_to_array(useState(false), 2), isClosed = _useState[0], setIsClosed = _useState[1];
    var recordMilestone = useRecordMilestone();
    var onViewRef = useDoActionWhenVisible(function() {
        getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_SEEN").forEach(function(milestone) {
            recordMilestone(milestone);
        });
    });
    var images = generateCSSBreakpointMapForImages(layoutElement.images);
    var _images_mobile, _images_tablet;
    var backgroundImage = useResponsiveValue({
        mobileAndAbove: {
            $backgroundImageUrl: (_this = (_images_mobile = images.mobile) !== null && _images_mobile !== void 0 ? _images_mobile : layoutElement.background_image) === null || _this === void 0 ? void 0 : _this.url,
            $height: MOBILE_IMAGE_HEIGHT
        },
        tabletAndAbove: {
            $backgroundImageUrl: (_this1 = (_images_tablet = images.tablet) !== null && _images_tablet !== void 0 ? _images_tablet : layoutElement.background_image) === null || _this1 === void 0 ? void 0 : _this1.url,
            $width: DESKTOP_IMAGE_WIDTH
        }
    });
    if (isClosed) {
        return null;
    }
    var ecoFriendlyIconTextListItems = (_layoutElement_layout_element_data = layoutElement.layout_element_data) === null || _layoutElement_layout_element_data === void 0 ? void 0 : _layoutElement_layout_element_data.map(function(data) {
        return data === null || data === void 0 ? void 0 : data.icon_text_list_item;
    }).filter(isNotUndefined);
    var uponDismissMilestones = getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_DISMISS");
    var shouldShowCloseButton = uponDismissMilestones.length > 0;
    var onCloseClick = function() {
        setIsClosed(true);
        uponDismissMilestones.forEach(function(milestone) {
            recordMilestone(milestone);
        });
    };
    var onCTAButtonClick = function() {
        getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_CLICK").forEach(function(m) {
            recordMilestone(m);
        });
        var _layoutElement_on_click_event_name;
        onCTAClick === null || onCTAClick === void 0 ? void 0 : onCTAClick((_layoutElement_on_click_event_name = layoutElement.on_click_event_name) !== null && _layoutElement_on_click_event_name !== void 0 ? _layoutElement_on_click_event_name : "MarketingBannerClick", requestId);
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        ref: onViewRef,
        children: [
            /*#__PURE__*/ _jsx(BannerImage, _object_spread({
                "data-test-id": "marketing-banner-image",
                $isMobile: !isTabletAndAbove,
                $isSSR: isSSR
            }, backgroundImage)),
            /*#__PURE__*/ _jsxs(Content, {
                children: [
                    layoutElement.tagline_text ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansMedium",
                                children: layoutElement.tagline_text
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "2x"
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "displaySSerifRegular",
                        children: layoutElement.main_text
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansRegular",
                        children: layoutElement.description_text
                    }),
                    isTabletAndAbove && !isEmpty(ecoFriendlyIconTextListItems) ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "2x"
                            }),
                            ecoFriendlyIconTextListItems.map(function(listItem, index) {
                                return /*#__PURE__*/ _jsx(ListItem, {
                                    listItem: listItem
                                }, index + "".concat(listItem.slate_icon));
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(CTAButton, {
                        linkTo: layoutElement.cta_link,
                        onClick: onCTAButtonClick,
                        children: layoutElement.cta_text
                    })
                ]
            }),
            shouldShowCloseButton ? /*#__PURE__*/ _jsx(CloseIconButton, {
                "data-test-id": "close-button",
                onClick: onCloseClick,
                children: /*#__PURE__*/ _jsx(Close, {
                    fontSize: "12px",
                    color: Core.icon.subdued
                })
            }) : null
        ]
    });
};
var Wrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row-reverse"
    }
}).withConfig({
    componentId: "sc-54de4b8d-0"
})([
    "overflow:hidden;position:relative;",
    "{margin:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing("3x", 0, "2x"));
var Content = styled(Column).withConfig({
    componentId: "sc-54de4b8d-1"
})([
    "background-color:",
    ";width:100%;padding:",
    ";",
    "{width:50%;padding:",
    ";}"
], Decorative.tint.neutral, getSpacing("4x"), responsiveMediaQueries.tabletAndAbove, getSpacing("6x"));
var getBackgroundUrl = function(param) {
    var $isMobile = param.$isMobile, $isSSR = param.$isSSR, _param_$backgroundImageUrl = param.$backgroundImageUrl, $backgroundImageUrl = _param_$backgroundImageUrl === void 0 ? EAEAEA_PIXEL : _param_$backgroundImageUrl, $width = param.$width, $height = param.$height;
    if ($isMobile && $height) {
        return optimizeImageUrlHeight({
            url: $backgroundImageUrl
        }, $isSSR, $height);
    } else if ($width) {
        return optimizeImageUrl({
            url: $backgroundImageUrl
        }, $isSSR, {
            width: $width
        });
    }
    return undefined;
};
var BannerImage = styled.div.withConfig({
    componentId: "sc-54de4b8d-2"
})([
    "display:",
    ";flex-shrink:0;width:100%;height:",
    "px;background-image:url(",
    ");background-size:cover;background-position:center;",
    "{width:50%;height:auto;}"
], function(getBackgroundUrl) {
    return !getBackgroundUrl.$backgroundImageUrl ? "none" : undefined;
}, MOBILE_IMAGE_HEIGHT, getBackgroundUrl, responsiveMediaQueries.tabletAndAbove);
var CTAButton = styled(Button).withConfig({
    componentId: "sc-54de4b8d-3"
})([
    "width:100%;position:unset;",
    '{width:fit-content;}&:before{content:" ";position:absolute;top:0;left:0;width:100%;height:100%;}'
], responsiveMediaQueries.tabletAndAbove);
var IconWrapper = styled(Column).withConfig({
    componentId: "sc-54de4b8d-4"
})([
    "margin:2px 12px 0 0;"
]);
var CloseIconButton = styled.div.withConfig({
    componentId: "sc-54de4b8d-5"
})([
    "cursor:pointer;position:absolute;right:",
    ";top:",
    ";"
], getSpacing("1x"), getSpacing("1x"));
